import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Parse a YYYY-MM-DD date string and format it without timezone conversion.
 * Using new Date('YYYY-MM-DD') parses as UTC midnight, which shifts one day
 * backward for users west of UTC. This helper uses the local-time constructor
 * so the displayed date always matches the stored calendar date.
 *
 * 2026-05-30 audit hardening: defensively handle bad input so callers don't
 * crash on null/undefined and don't leak "Invalid Date" into Christa's UI.
 *   - null / undefined / '' → ''  (let the caller decide on a placeholder)
 *   - full ISO timestamp ('YYYY-MM-DDTHH:...') → format the date portion only
 *   - malformed (non-numeric, wrong shape) → ''
 */
export function formatDateOnly(
  dateStr: string | null | undefined,
  opts: Intl.DateTimeFormatOptions = { month: 'numeric', day: 'numeric', year: 'numeric' },
): string {
  if (!dateStr || typeof dateStr !== 'string') return '';
  // Accept full ISO timestamps by taking only the date portion.
  const datePart = dateStr.slice(0, 10);
  const parts = datePart.split('-');
  if (parts.length !== 3) return '';
  const [y, m, d] = parts.map(Number);
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return '';
  if (m < 1 || m > 12 || d < 1 || d > 31) return '';
  const dt = new Date(y, m - 1, d);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString('en-US', opts);
}